import { StudentProjectCard, deserify, useCommonServiceClientContext } from '@geneo2-web/shared-ui';
import { SxProps } from '@mui/material';
import { StudentAssessmentTask } from '@protos/learning_management/lms.assessment.student.apis_pb';
import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../reduxStore/reduxHooks';

interface IProps {
  project: StudentAssessmentTask;
  onClick?: () => void;
  rootStyles?: SxProps;
  isEnded?: boolean;
}

/**
 * Wraps the presentational `StudentProjectCard` and lazily loads the student's
 * collaboration group for the project when the details popup is opened.
 *
 * Student APIs expose only the group's name (not its members), so the popup
 * shows Group Name and Created By.
 *
 * The loaded data is tracked against its assessment id, so if React reuses this
 * instance for a different card the stale group is dropped and refetched.
 */
export const StudentProjectCardWithGroup = ({
  project,
  onClick,
  rootStyles,
  isEnded,
}: IProps) => {
  const { LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const studentId = deserify(
    useAppSelector((state) => state.auth.userInfo?.studentProfileId)
  );

  const assessmentId = project?.assessmentId ? project.assessmentId.toString() : undefined;

  const [groupName, setGroupName] = useState<string>();
  const [loading, setLoading] = useState(false);
  /** Assessment id the current `groupName` belongs to (null until loaded, or on a mismatch). */
  const loadedForRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  /** Latest assessment id, to discard responses that arrive after the card is reused. */
  const currentIdRef = useRef(assessmentId);
  currentIdRef.current = assessmentId;

  // Drop stale data if this instance gets reused for a different project.
  useEffect(() => {
    if (loadedForRef.current !== null && loadedForRef.current !== assessmentId) {
      loadedForRef.current = null;
      inFlightRef.current = false;
      setGroupName(undefined);
      setLoading(false);
    }
  }, [assessmentId]);

  const loadGroupDetails = async () => {
    if (!assessmentId || !studentId) return;
    if (inFlightRef.current) return;
    if (loadedForRef.current === assessmentId) return; // already loaded for this card

    inFlightRef.current = true;
    setLoading(true);
    try {
      const response =
        await LmsAssessmentStudentApiServiceV2ClientWithStatusCodeHandler.fetchStudentAssessmentResponse(
          {
            studentId: BigInt(studentId),
            assessmentId: BigInt(assessmentId),
          }
        );
      // Ignore a response that arrived after the card was reused for another project.
      if (currentIdRef.current === assessmentId) {
        loadedForRef.current = assessmentId;
        setGroupName(response?.data?.groupName);
      }
    } catch (error) {
      console.error('Failed to load project group details', error);
      setGroupName(undefined);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  return (
    <StudentProjectCard
      project={project}
      onClick={onClick}
      rootStyles={rootStyles}
      isEnded={isEnded}
      createdBy={project?.teacherName}
      groupName={groupName}
      groupDetailsLoading={loading}
      onDetailsOpen={loadGroupDetails}
    />
  );
};

export default StudentProjectCardWithGroup;
